import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Chapter, NewChapter, UpdateChapter } from '@fsp/types';
import {
  fetchChapters,
  fetchChapter,
  createChapter,
  updateChapter,
  deleteChapter,
  reorderChaptersBulk,
} from '../../services/chapter';

// Query keys
export const chapterKeys = {
  all: ['chapters'] as const,
  lists: () => [...chapterKeys.all, 'list'] as const,
  list: (orgId: string, planId: string) => [...chapterKeys.lists(), orgId, planId] as const,
  details: () => [...chapterKeys.all, 'detail'] as const,
  detail: (orgId: string, planId: string, chapterId: string) =>
    [...chapterKeys.details(), orgId, planId, chapterId] as const,
};

// Queries
export const useChapters = (organisationId: string | undefined, planId: string | undefined) => {
  return useQuery({
    queryKey: chapterKeys.list(organisationId!, planId!),
    queryFn: ({ signal }) => fetchChapters(organisationId!, planId!, signal),
    enabled: !!organisationId && !!planId,
    placeholderData: (previousData) => previousData,
  });
};

export const useChapter = (
  organisationId: string | undefined,
  planId: string | undefined,
  chapterId: string | undefined
) => {
  return useQuery({
    queryKey: chapterKeys.detail(organisationId!, planId!, chapterId!),
    queryFn: ({ signal }) => fetchChapter(organisationId!, planId!, chapterId!, signal),
    enabled: !!organisationId && !!planId && !!chapterId,
    placeholderData: (previousData) => previousData,
  });
};

// Mutations
export const useCreateChapter = (organisationId: string, planId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NewChapter) => createChapter(organisationId, planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chapterKeys.list(organisationId, planId) });
    },
  });
};

export const useUpdateChapter = (organisationId: string, planId: string, chapterId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateChapter) => updateChapter(organisationId, planId, chapterId, data),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({
        queryKey: chapterKeys.detail(organisationId, planId, chapterId),
      });

      const previousChapter = queryClient.getQueryData(
        chapterKeys.detail(organisationId, planId, chapterId)
      );

      if (previousChapter) {
        queryClient.setQueryData(chapterKeys.detail(organisationId, planId, chapterId), {
          ...previousChapter,
          chapter: { ...(previousChapter as any).chapter, ...newData },
        });
      }

      return { previousChapter };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousChapter) {
        queryClient.setQueryData(
          chapterKeys.detail(organisationId, planId, chapterId),
          context.previousChapter
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: chapterKeys.detail(organisationId, planId, chapterId),
      });
      queryClient.invalidateQueries({ queryKey: chapterKeys.list(organisationId, planId) });
    },
  });
};

export const useDeleteChapter = (organisationId: string, planId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chapterId: string) => deleteChapter(organisationId, planId, chapterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chapterKeys.list(organisationId, planId) });
    },
  });
};

export const useReorderChapters = (organisationId: string, planId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chapters: { id: string; sortOrder: number }[]) =>
      reorderChaptersBulk(organisationId, planId, chapters),
    onMutate: async (newOrder) => {
      await queryClient.cancelQueries({ queryKey: chapterKeys.list(organisationId, planId) });

      const previousChapters = queryClient.getQueryData<Chapter[]>(
        chapterKeys.list(organisationId, planId)
      );

      // Optimistically update
      if (previousChapters) {
        const updatedChapters = previousChapters.map((chapter) => {
          const newSort = newOrder.find((c) => c.id === chapter.id);
          return newSort ? { ...chapter, sortOrder: newSort.sortOrder } : chapter;
        });
        queryClient.setQueryData(chapterKeys.list(organisationId, planId), updatedChapters);
      }

      return { previousChapters };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousChapters) {
        queryClient.setQueryData(
          chapterKeys.list(organisationId, planId),
          context.previousChapters
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: chapterKeys.list(organisationId, planId) });
    },
  });
};
